import { ReactNode } from 'react';
import { Body1Strong, Caption1, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { Accept, useDropzone } from 'react-dropzone';

type TProps = {
  icon: ReactNode;
  /** Comma-separated MIME types (e.g. 'application/pdf,text/csv'). Non-matching files - including ones nested inside a dropped folder - are filtered out silently. */
  accept?: string;
  label: string;
  /** Optional smaller caption rendered under the label, typically used for format/size constraints. */
  hint?: string;
  multiple?: boolean;
  /** When true, the zone rejects both drops and click-to-browse and renders with a muted style. */
  disabled?: boolean;
  /** Fired with the flattened list of accepted files (folders are recursively enumerated). */
  onFiles?: (files: File[]) => void;
};

const useStyles = makeStyles({
  zone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: tokens.spacingVerticalS,
    width: '100%',
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    ...shorthands.transition('border-color, color, background-color', '120ms', '0s', 'ease'),
    ':hover': {
      ...shorthands.borderColor(tokens.colorNeutralStroke1Hover),
      color: tokens.colorNeutralForeground2,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      ...shorthands.outline('2px', 'solid', tokens.colorStrokeFocus2),
      outlineOffset: '2px',
    },
  },
  zoneActive: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorBrandBackground2,
  },
  zoneDisabled: {
    cursor: 'not-allowed',
    opacity: '0.5',
    ':hover': {
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
      color: tokens.colorNeutralForeground3,
      backgroundColor: tokens.colorNeutralBackground1,
    },
    // react-dropzone's `disabled: true` removes interactivity, but the focus ring would still render
    // if the element is reached via keyboard - suppress it so the visual matches the semantic state.
    ':focus-visible': {
      ...shorthands.outline('0'),
    },
  },
  icon: {
    color: tokens.colorNeutralForeground3,
  },
});

// Listing the extension next to the MIME type catches files whose browser-reported MIME is empty or
// generic - Windows with Excel installed sends a .csv as application/vnd.ms-excel, and Safari file
// drops often send nothing at all.
const EXTENSIONS_BY_MIME: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
};

const buildAcceptConfig = (accept: string | undefined): Accept | undefined => {
  if (!accept) return undefined;
  const mimeTypes = accept.split(',').map((type) => type.trim());
  return Object.fromEntries(mimeTypes.map((type) => [type, EXTENSIONS_BY_MIME[type] ?? []]));
};

const FileDropZone = ({ icon, label, hint, accept, multiple = false, disabled = false, onFiles }: TProps) => {
  const classes = useStyles();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) onFiles?.(acceptedFiles);
    },
    accept: buildAcceptConfig(accept),
    multiple,
    disabled,
  });

  return (
    <div
      {...getRootProps({
        className: mergeClasses(
          classes.zone,
          isDragActive && !disabled && classes.zoneActive,
          disabled && classes.zoneDisabled
        ),
      })}
    >
      <input {...getInputProps()} />
      <span className={classes.icon}>{icon}</span>
      <Body1Strong>{label}</Body1Strong>
      {hint && <Caption1>{hint}</Caption1>}
    </div>
  );
};

export { FileDropZone };
